import { Button, Dialog, IconButton } from "@au-schein-de/au-ui-library";
import type { AddressResult } from "@dransay/address-validation";
import { LoaderCircleIcon, MapPinIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CrosshairIcon } from "~/components/icons/crosshair-icon";
import { MobileAddressAutocomplete } from "~/components/mobile-address-autocomplete.component";
import { TransformedImage } from "~/components/transformed-image.component";
import { COURIER_DELIVERY_ILLUSTRATION_SRC } from "~/constants/courier-delivery-illustration";
import type { DeliveryAddressCookieData } from "~/features/delivery-address/delivery-address.cookie";

interface DeliveryAddressModalProps {
	open: boolean;
	onClose: () => void;
	currentAddress: DeliveryAddressCookieData | null;
	onSave: (address: AddressResult) => void;
	suggestions: AddressResult[];
	onSearch: (query: string) => void;
	onRequestLocation: () => void;
	isLoading?: boolean;
	isLocating?: boolean;
	isAuthenticated?: boolean;
}

function formatSuggestion(address: AddressResult): string {
	const street = [address.street, address.houseNumber]
		.filter(Boolean)
		.join(" ");
	const location = [address.postalCode, address.city].filter(Boolean).join(" ");
	return [street, location].filter(Boolean).join(", ");
}

export function DeliveryAddressModal({
	open,
	onClose,
	currentAddress,
	onSave,
	suggestions,
	onSearch,
	onRequestLocation,
	isLoading = false,
	isLocating = false,
	isAuthenticated = false,
}: Readonly<DeliveryAddressModalProps>) {
	const { t } = useTranslation("common");
	const [query, setQuery] = useState("");
	const [selectedAddress, setSelectedAddress] = useState<AddressResult | null>(
		null,
	);
	const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const [showError, setShowError] = useState(false);
	const desktopInputRef = useRef<HTMLInputElement>(null);
	const desktopSuggestionsRef = useRef<HTMLDivElement>(null);
	const justOpenedRef = useRef(false);

	const shouldShowDesktopSuggestions = useMemo(
		() => query.trim().length > 0 && suggestions.length > 0,
		[query, suggestions.length],
	);

	const totalDesktopItems = suggestions.length + 1;

	const resetSearchState = useCallback(() => {
		setQuery("");
		setSelectedAddress(null);
		setHighlightedIndex(-1);
		setMobileSearchOpen(false);
		setShowError(false);
	}, []);

	useEffect(() => {
		if (open) {
			// Pre-fill the input with the current address so the user can see
			// the full address they previously entered (MKP-1244).
			setQuery(currentAddress?.displayString ?? "");
			justOpenedRef.current = true;
		} else {
			resetSearchState();
		}
	}, [open, currentAddress, resetSearchState]);

	// After the pre-filled value commits, collapse the caret to the end so the
	// address shows as plain text and never appears selected/highlighted
	// (MKP-1244). Guarded so it only runs once on open, not on every keystroke.
	useEffect(() => {
		if (!justOpenedRef.current) {
			return;
		}
		justOpenedRef.current = false;
		desktopInputRef.current?.setSelectionRange(query.length, query.length);
	}, [query]);

	useEffect(() => {
		if (!shouldShowDesktopSuggestions || highlightedIndex < 0) {
			return;
		}
		const container = desktopSuggestionsRef.current;
		if (!container) {
			return;
		}
		const highlightedItem = container.children.item(highlightedIndex);
		if (!(highlightedItem instanceof HTMLElement)) {
			return;
		}
		highlightedItem.scrollIntoView?.({ block: "nearest" });
	}, [highlightedIndex, shouldShowDesktopSuggestions]);

	const handleQueryChange = (nextQuery: string) => {
		setQuery(nextQuery);
		onSearch(nextQuery);
		setHighlightedIndex(-1);
		setShowError(false);
		if (selectedAddress && nextQuery !== formatSuggestion(selectedAddress)) {
			setSelectedAddress(null);
		}
	};

	// The address behaves like a single token: a single deletion (e.g. one
	// backspace) clears the whole input rather than removing one character
	// (MKP-1244). Mirrors the mobile `delivery` variant behaviour.
	const handleDesktopInputChange = (nextValue: string) => {
		const isDeletion = nextValue.length < query.length;
		handleQueryChange(isDeletion ? "" : nextValue);
	};

	const handleSuggestionSelect = useCallback(
		(address: AddressResult) => {
			onSave(address);
			onClose();
		},
		[onSave, onClose],
	);

	const handleDesktopKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (!shouldShowDesktopSuggestions) {
				return;
			}

			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					setHighlightedIndex((prev) =>
						prev < totalDesktopItems - 1 ? prev + 1 : 0,
					);
					break;
				case "ArrowUp":
					event.preventDefault();
					setHighlightedIndex((prev) =>
						prev > 0 ? prev - 1 : totalDesktopItems - 1,
					);
					break;
				case "Enter":
					event.preventDefault();
					if (highlightedIndex === 0) {
						onRequestLocation();
					} else if (highlightedIndex > 0) {
						handleSuggestionSelect(suggestions[highlightedIndex - 1]);
					}
					break;
				case "Escape":
					setQuery("");
					setHighlightedIndex(-1);
					break;
			}
		},
		[
			shouldShowDesktopSuggestions,
			totalDesktopItems,
			highlightedIndex,
			suggestions,
			onRequestLocation,
			handleSuggestionSelect,
		],
	);

	const handleDesktopSave = () => {
		if (!selectedAddress) {
			setShowError(true);
			return;
		}
		onSave(selectedAddress);
		onClose();
	};

	const saveButtonLabel = currentAddress
		? t("header.addressModal.changeButton")
		: t("header.addressModal.saveButton");

	return (
		<Dialog.Root
			open={open}
			onOpenChange={(nextOpen) => {
				if (nextOpen) {
					return;
				}
			}}
		>
			<Dialog.Content
				withCloseButton={false}
				data-delivery-address-modal
				className={`z-[110] !block !p-0 !gap-0 !translate-y-0 border-none ${
					mobileSearchOpen
						? "!inset-0 !mx-0 !w-full !h-full !max-w-none !max-h-none !rounded-none !shadow-none"
						: "!top-0 md:!top-[120px] [@media(max-height:839px)]:md:!overflow-hidden [@media(max-height:839px)]:md:!max-h-[calc(100dvh-120px-1rem)] !rounded-2xl shadow-[0px_12px_40px_0px_rgba(20,22,19,0.25)]"
				}`}
				aria-describedby={undefined}
				onOpenAutoFocus={(event) => {
					event.preventDefault();
					const input = desktopInputRef.current;
					input?.focus();
					// Defer one frame so the pre-filled value has committed and any
					// focus-time text selection (Radix/browser) is cleared — the
					// address must show as plain text, not selected (MKP-1244).
					requestAnimationFrame(() => {
						if (!input) {
							return;
						}
						input.setSelectionRange(input.value.length, input.value.length);
					});
				}}
				onInteractOutside={(event) => {
					event.preventDefault();
				}}
			>
				<Dialog.Title className="sr-only">
					{t("header.addressModal.title")}
				</Dialog.Title>
				<div
					data-testid="delivery-address-modal"
					className="relative flex w-full md:w-[570px] md:max-w-full flex-col items-center bg-white pb-8 md:pb-12 rounded-xl [@media(max-height:839px)]:md:max-h-[calc(100dvh-120px-1rem)] [@media(max-height:839px)]:md:overflow-y-auto [@media(max-height:839px)]:md:overflow-x-hidden"
				>
					<div className="flex w-full items-center justify-end p-4">
						<IconButton
							shape="circle"
							color="gray"
							icon={<XIcon size={20} />}
							onClick={onClose}
							aria-label={t("close")}
							data-testid="delivery-address-modal-close"
							className="!text-gray-700"
						/>
					</div>

					<TransformedImage
						src={COURIER_DELIVERY_ILLUSTRATION_SRC}
						alt=""
						width={96}
						height={96}
						loading="lazy"
						className="size-24 shrink-0 object-contain"
					/>

					<div className="flex w-full flex-col gap-6 items-center px-4 md:px-10 pt-3 md:pt-6">
						<div className="flex w-full flex-col gap-2">
							<h2
								className="text-h4 md:text-h3 text-gray-700"
								data-testid="delivery-address-modal-title"
							>
								{t("header.addressModal.title")}
							</h2>
							<p className="text-body text-gray-500">
								{t("header.addressModal.subtitle")}
							</p>
						</div>

						{/* Desktop: inline search input with autocomplete */}
						<search className="hidden md:block w-full">
							<div
								className={`flex h-12 items-center gap-2 overflow-hidden rounded-xl bg-gray-100 px-4 border-2 ${showError ? "border-error-600" : "border-transparent focus-within:border-ansay-main"}`}
							>
								<MapPinIcon
									size={24}
									className="shrink-0 text-gray-700"
									aria-hidden="true"
								/>
								<input
									ref={desktopInputRef}
									data-testid="delivery-address-modal-desktop-input"
									className="text-body min-w-0 flex-1 bg-transparent text-gray-700 outline-none placeholder:text-gray-700"
									value={query}
									placeholder={t("header.addressModal.searchPlaceholder")}
									aria-label={t("header.addressModal.searchPlaceholder")}
									onChange={(event) => {
										handleDesktopInputChange(event.currentTarget.value);
									}}
									onKeyDown={handleDesktopKeyDown}
								/>
								<button
									type="button"
									data-testid="delivery-address-modal-crosshair"
									aria-label={t("header.addressModal.useCurrentLocation")}
									className="shrink-0 cursor-pointer text-ansay-main disabled:opacity-50 disabled:cursor-not-allowed"
									onClick={onRequestLocation}
									disabled={isLocating}
								>
									{isLocating ? (
										<LoaderCircleIcon
											width={24}
											height={24}
											className="animate-spin"
											aria-hidden="true"
										/>
									) : (
										<CrosshairIcon width={24} height={24} aria-hidden="true" />
									)}
								</button>
							</div>

							{showError && (
								<p
									className="mt-0.5 text-body-sm text-error-500"
									data-testid="delivery-address-modal-error"
								>
									{t("header.addressModal.emptyAddressError")}
								</p>
							)}

							{shouldShowDesktopSuggestions && (
								<div
									ref={desktopSuggestionsRef}
									data-testid="delivery-address-modal-desktop-suggestions"
									className="mt-1"
								>
									<button
										type="button"
										className={`flex w-full items-center gap-3 border-b border-gray-200 px-4 py-3 text-left cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${highlightedIndex === 0 ? "bg-gray-100" : "hover:bg-gray-50"}`}
										data-testid="delivery-address-modal-desktop-use-location"
										onClick={onRequestLocation}
										onMouseEnter={() => setHighlightedIndex(0)}
										disabled={isLocating}
									>
										{isLocating ? (
											<LoaderCircleIcon
												width={20}
												height={20}
												className="shrink-0 animate-spin text-ansay-main"
												aria-hidden="true"
											/>
										) : (
											<CrosshairIcon
												width={20}
												height={20}
												className="shrink-0 text-ansay-main"
												aria-hidden="true"
											/>
										)}
										<span className="flex flex-col">
											<span className="text-body-bold text-gray-700">
												{t("header.addressModal.useCurrentLocation")}
											</span>
											<span className="text-caption text-gray-500">
												{t("header.addressModal.addLater")}
											</span>
										</span>
									</button>
									{suggestions.map((address, index) => (
										<button
											key={`${address.id}-${index}`}
											type="button"
											className={`text-body block w-full border-b border-gray-200 px-4 py-3 text-left text-gray-700 last:border-b-0 cursor-pointer ${highlightedIndex === index + 1 ? "bg-gray-100" : "hover:bg-gray-50"}`}
											data-testid={`delivery-address-modal-desktop-suggestion-${index}`}
											onClick={() => {
												handleSuggestionSelect(address);
											}}
											onMouseEnter={() => setHighlightedIndex(index + 1)}
										>
											{formatSuggestion(address)}
										</button>
									))}
								</div>
							)}
						</search>

						{/* Mobile: button trigger that opens full-screen search */}
						<div className="flex md:hidden w-full flex-col">
							<div
								className={`flex h-12 w-full items-center gap-2 rounded-xl border-2 bg-gray-100 px-4 ${showError ? "border-error-600" : "border-transparent"}`}
							>
								<button
									type="button"
									data-testid="delivery-address-modal-mobile-search-trigger"
									aria-label={t("header.addressModal.searchPlaceholder")}
									className="flex min-w-0 flex-1 items-center gap-2 cursor-pointer"
									onClick={() => {
										setMobileSearchOpen(true);
										setShowError(false);
									}}
								>
									<MapPinIcon
										size={24}
										className="shrink-0 text-gray-700"
										aria-hidden="true"
									/>
									<span className="text-body min-w-0 flex-1 text-left text-gray-700">
										{query || t("header.addressModal.searchPlaceholder")}
									</span>
								</button>
								<button
									type="button"
									data-testid="delivery-address-modal-mobile-crosshair"
									aria-label={t("header.addressModal.useCurrentLocation")}
									className="shrink-0 cursor-pointer text-ansay-main disabled:opacity-50 disabled:cursor-not-allowed"
									onClick={onRequestLocation}
									disabled={isLocating}
								>
									{isLocating ? (
										<LoaderCircleIcon
											width={24}
											height={24}
											className="animate-spin"
											aria-hidden="true"
										/>
									) : (
										<CrosshairIcon width={24} height={24} aria-hidden="true" />
									)}
								</button>
							</div>
							{showError && (
								<p
									className="mt-0.5 text-body-sm text-error-500"
									data-testid="delivery-address-modal-mobile-error"
								>
									{t("header.addressModal.emptyAddressError")}
								</p>
							)}
						</div>

						{!shouldShowDesktopSuggestions && (
							<div className="flex w-full flex-col gap-6">
								<Button
									type="button"
									variant="raw"
									className="text-body-emphasis h-12 md:h-14 w-full cursor-pointer rounded-xl bg-ansay-light text-gray-700"
									data-testid="delivery-address-modal-save"
									onClick={handleDesktopSave}
								>
									{saveButtonLabel}
								</Button>

								{!isAuthenticated && (
									<div className="flex items-center justify-center gap-1">
										<span className="text-body text-gray-500">
											{t("header.addressModal.haveOrderedBefore")}
										</span>
										<a
											href="/auth/login"
											className="text-body-bold text-ansay-700"
											data-testid="delivery-address-modal-login-link"
										>
											{t("header.addressModal.login")}
										</a>
									</div>
								)}
							</div>
						)}
					</div>
				</div>

				<MobileAddressAutocomplete
					open={mobileSearchOpen}
					onClose={() => {
						setQuery("");
						onSearch("");
						setSelectedAddress(null);
						setMobileSearchOpen(false);
					}}
					query={query}
					onQueryChange={handleQueryChange}
					suggestions={suggestions}
					onSuggestionSelect={handleSuggestionSelect}
					variant="delivery"
					renderMode="inline"
					isLoading={isLoading}
					onRequestLocation={onRequestLocation}
					isLocating={isLocating}
					placeholder={t("header.addressModal.searchPlaceholder")}
				/>
			</Dialog.Content>
		</Dialog.Root>
	);
}
